import React, { FC, ReactNode } from "react"

interface Props {
    hasSelectorUserValid?: true
    hasSelectorUserInvalid?: true
    doesNotHaveSelectorUserValid?: true
    doesNotHaveSelectorUserInvalid?: true
    children: ReactNode
}
/**
 * @description
 * Can be used only:
 * - as a direct sibling to an input element 
 * - as a direct sibling to an element that contains a single input element (at any depth).
 * - as a direct sibling to another component using the o-validation-message class (HiddenWhenSibling or
 *   ValidationMessage), in which case it responds to the same input element
 *
 * @param hasSelectorUserValid -- hides content when the sibling input is valid AFTER user interaction
 * @param hasSelectorUserInvalid -- hides content when the sibling input is invalid AFTER user interaction
 * @param doesNotHaveSelectorUserValid -- hides content when the sibling input is either valid or invalid 
 * BEFORE user interaction OR is invalid AFTER user interaction
 * @param doesNotHaveSelectorUserInvalid -- hides content when the sibling input is either valid or invalid 
 * BEFORE user interaction OR is valid AFTER user interaction
 *
 * for more information: {@link https://developer.mozilla.org/en-US/docs/Web/CSS/:user-valid}
 *
 * */
export const HiddenWhenSiblingInput: FC<Props> = ({
    hasSelectorUserValid,
    hasSelectorUserInvalid,
    doesNotHaveSelectorUserValid,
    doesNotHaveSelectorUserInvalid,
    children,
}) => {
    const baseClassName = "o-validation-messages"
    const hiddenWhenValidClassNames = hasSelectorUserValid ? ".o-validation-messages--hidden-when-valid" : undefined
    const hiddenWhenNotValidClassNames = !!doesNotHaveSelectorUserValid ? "o-validation-messages--hidden-when-not-valid" : undefined
    const hiddenWhenInvalidClassNames = !!hasSelectorUserInvalid ? "o-validation-messages--hidden-when-invalid" : undefined
    const hiddenWhenNotInvalidClassNames = !!doesNotHaveSelectorUserInvalid ? "o-validation-messages--hidden-when-not-invalid" : undefined
    const className = [
        baseClassName,
        hiddenWhenValidClassNames,
        hiddenWhenNotValidClassNames,
        hiddenWhenInvalidClassNames,
        hiddenWhenNotInvalidClassNames,
    ].filter((className) => !!className).join(' ')

    return (
        <div className={className}>
            {children}
        </div>
    )
}
